// src/products/capRaise/components/AddInvestorModal.js
// Modal for adding investors to a CapRaise round

import React, { useState } from 'react';
import { X, Plus, Upload } from 'lucide-react';

const AddInvestorModal = ({ isOpen, onClose, onSave, loading }) => {
  const [newLeads, setNewLeads] = useState([{ name: '', email: '', company: '' }]);

  // Add a new lead row
  const addLeadRow = () => {
    setNewLeads([...newLeads, { name: '', email: '', company: '' }]);
  };

  // Remove a lead row
  const removeLeadRow = (index) => {
    const updated = newLeads.filter((_, i) => i !== index);
    setNewLeads(updated.length > 0 ? updated : [{ name: '', email: '', company: '' }]);
  };

  // Update lead field
  const updateLeadField = (index, field, value) => {
    const updated = [...newLeads];
    updated[index][field] = value;
    setNewLeads(updated);
  };

  // Handle save
  const handleSave = () => {
    const validLeads = newLeads.filter(lead => 
      lead.name.trim() && lead.email.trim() && lead.company.trim()
    );
    
    if (validLeads.length === 0) {
      return;
    }
    
    onSave(validLeads);
  };

  // Reset form when modal closes
  const handleClose = () => {
    setNewLeads([{ name: '', email: '', company: '' }]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Add Investors</h2>
            <p className="text-sm text-gray-600">Add investor information before sending invitations</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Add Row Button */}
          <div className="flex justify-end">
            <button
              onClick={addLeadRow}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Row
            </button>
          </div>

          {/* Lead Forms */}
          <div className="space-y-3">
            {newLeads.map((lead, index) => (
              <div key={index} className="flex gap-3">
                <input
                  type="text"
                  placeholder="Name"
                  value={lead.name}
                  onChange={(e) => updateLeadField(index, 'name', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={lead.email}
                  onChange={(e) => updateLeadField(index, 'email', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  type="text"
                  placeholder="Company"
                  value={lead.company}
                  onChange={(e) => updateLeadField(index, 'company', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                {newLeads.length > 1 && (
                  <button
                    onClick={() => removeLeadRow(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* CSV Import Section */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Import from CSV</h3>
              <Upload className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-sm text-gray-600 mb-4">Upload a CSV file with columns: Name, Email, Company</p>
            <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">
              Choose File
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading || newLeads.every(lead => !lead.name.trim() || !lead.email.trim() || !lead.company.trim())}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Investors'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddInvestorModal;